export const getExpectedEpisodesPerRecording = (
  totalEpisodes,
  recordingsCount,
  plannedEpisodesPerRecording
) => {
  if (plannedEpisodesPerRecording && plannedEpisodesPerRecording > 0) {
    return plannedEpisodesPerRecording;
  }

  if (!totalEpisodes || !recordingsCount || recordingsCount <= 0) return null;
  return Math.round(totalEpisodes / recordingsCount);
};

const normalizeForComparison = (value) =>
  (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

/** True when billing type is per-recording (e.g. «Ανά εγγραφή»). */
export const isPerRecordingBillingType = (pciTypeId, billingTypes) => {
  if (!pciTypeId || !billingTypes?.length) return false;
  const type = billingTypes.find((entry) => entry.id === pciTypeId);
  if (!type?.pcit_descr) return false;
  const normalized = normalizeForComparison(type.pcit_descr);
  return normalized.includes("εγγραφ");
};

export const hasRecordingEpisodeCountMismatch = (recordingEpisodeCount, expectedPerRecording) => {
  if (!expectedPerRecording || !recordingEpisodeCount) return false;
  return recordingEpisodeCount !== expectedPerRecording;
};

export const getSuggestedPerEpisodeForRecording = (
  pciAmount,
  expectedPerRecording,
  actualEpisodeCount
) => {
  if (!pciAmount || !expectedPerRecording || !actualEpisodeCount) return null;
  return Math.round(((pciAmount * expectedPerRecording) / actualEpisodeCount) * 100) / 100;
};

export const getRecordingBudgetMismatch = ({
  recording,
  pciAmount,
  currentPerEpisodeAmount,
  expectedEpisodesPerRecording,
  hasPrediction = false,
  tolerance = 0.5,
}) => {
  const episodes = Array.isArray(recording?.episodes) ? recording.episodes : [];
  if (episodes.length === 0 || !expectedEpisodesPerRecording) {
    return null;
  }

  const episodeCountMismatch = hasRecordingEpisodeCountMismatch(
    episodes.length,
    expectedEpisodesPerRecording
  );

  if (!episodeCountMismatch) return null;

  const suggestedPerEpisode = getSuggestedPerEpisodeForRecording(
    pciAmount,
    expectedEpisodesPerRecording,
    episodes.length
  );

  if (suggestedPerEpisode === null) return null;

  const differsFromBudget =
    currentPerEpisodeAmount != null &&
    pciAmount != null &&
    Math.abs(currentPerEpisodeAmount - pciAmount) > tolerance;

  const amountMismatch =
    hasPrediction &&
    currentPerEpisodeAmount != null &&
    Math.abs(currentPerEpisodeAmount - suggestedPerEpisode) > tolerance;

  return {
    episodeCount: episodes.length,
    expectedEpisodeCount: expectedEpisodesPerRecording,
    currentPerEpisode: currentPerEpisodeAmount,
    suggestedPerEpisode,
    episodeCountMismatch,
    amountMismatch,
    differsFromBudget,
    needsAttention:
      episodeCountMismatch &&
      !differsFromBudget &&
      (!hasPrediction || amountMismatch),
  };
};

export const formatRecordingEpisodeCountMessage = (mismatch) => {
  if (!mismatch) return '';
  return `Η εγγραφή έχει ${mismatch.episodeCount} επεισόδια (αναμενόμενα ~${mismatch.expectedEpisodeCount}).`;
};

export const formatRecordingPredictionMessage = (mismatch, formatCurrency) => {
  if (!mismatch?.amountMismatch) return '';
  const current = formatCurrency(mismatch.currentPerEpisode);
  const suggested = formatCurrency(mismatch.suggestedPerEpisode);
  return `Πρόβλεψη: ${current}/επ. — προτείνεται ${suggested}/επ.`;
};

export const formatRecordingMismatchTooltip = (mismatch, formatCurrency) => {
  if (!mismatch) return '';
  const parts = [formatRecordingEpisodeCountMessage(mismatch)];
  const predictionPart = formatRecordingPredictionMessage(mismatch, formatCurrency);
  if (predictionPart) parts.push(predictionPart);
  return parts.join(' ');
};
